import { createPopper } from '@popperjs/core'
import { defineComponent, h, onBeforeUnmount, onMounted, ref } from 'vue'

const TOOLTIP_OFFSET = [0, 8]

const createTooltipElement = (content, allowHtml, extraClass) => {
  const tooltipEl = document.createElement('div')
  tooltipEl.className = 'tooltip bs-tooltip-auto'
  if (extraClass) {
    tooltipEl.className += ` ${extraClass}`
  }
  tooltipEl.setAttribute('role', 'tooltip')

  const arrow = document.createElement('div')
  arrow.className = 'tooltip-arrow'
  const inner = document.createElement('div')
  inner.className = 'tooltip-inner'
  if (allowHtml) {
    inner.innerHTML = content || ''
  } else {
    inner.textContent = content || ''
  }

  tooltipEl.appendChild(arrow)
  tooltipEl.appendChild(inner)
  return tooltipEl
}

const normalizePlacement = (binding) => {
  if (binding.modifiers.left || binding.arg === 'left') return 'left'
  if (binding.modifiers.right || binding.arg === 'right') return 'right'
  if (binding.modifiers.bottom || binding.arg === 'bottom') return 'bottom'
  if (binding.modifiers.top || binding.arg === 'top') return 'top'
  return 'top'
}

const normalizeTriggers = (binding) => {
  if (binding.modifiers.click) return ['click']
  if (binding.modifiers.focus) return ['focus']
  if (binding.modifiers.hover || binding.arg === 'hover') return ['hover', 'focus']
  return ['hover', 'focus']
}

const setupTooltip = (el, binding, content, extraClass) => {
  const allowHtml = Boolean(binding.modifiers.html)
  const placement = normalizePlacement(binding)
  const triggers = normalizeTriggers(binding)

  let tooltipEl = null
  let popper = null

  const show = () => {
    if (!tooltipEl) {
      tooltipEl = createTooltipElement(content, allowHtml, extraClass)
      document.body.appendChild(tooltipEl)
      popper = createPopper(el, tooltipEl, {
        placement,
        modifiers: [{ name: 'offset', options: { offset: TOOLTIP_OFFSET } }]
      })
    }
    tooltipEl.classList.add('show')
    tooltipEl.style.display = 'block'
    if (popper) popper.update()
  }

  const hide = () => {
    if (tooltipEl) {
      tooltipEl.classList.remove('show')
      tooltipEl.style.display = 'none'
    }
  }

  const destroy = () => {
    if (popper) {
      popper.destroy()
      popper = null
    }
    if (tooltipEl && tooltipEl.parentNode) {
      tooltipEl.parentNode.removeChild(tooltipEl)
      tooltipEl = null
    }
  }

  const onClick = () => {
    if (tooltipEl && tooltipEl.style.display === 'block') {
      hide()
    } else {
      show()
    }
  }

  const listeners = []
  if (triggers.includes('hover')) {
    el.addEventListener('mouseenter', show)
    el.addEventListener('mouseleave', hide)
    listeners.push(['mouseenter', show], ['mouseleave', hide])
  }
  if (triggers.includes('focus')) {
    el.addEventListener('focus', show)
    el.addEventListener('blur', hide)
    listeners.push(['focus', show], ['blur', hide])
  }
  if (triggers.includes('click')) {
    el.addEventListener('click', onClick)
    listeners.push(['click', onClick])
  }

  return {
    update (newContent) {
      if (!tooltipEl) return
      const inner = tooltipEl.querySelector('.tooltip-inner')
      if (inner) {
        if (allowHtml) inner.innerHTML = newContent || ''
        else inner.textContent = newContent || ''
      }
      if (popper) popper.update()
    },
    destroy () {
      listeners.forEach(([event, handler]) => el.removeEventListener(event, handler))
      destroy()
    }
  }
}

const tooltipDirective = {
  mounted (el, binding) {
    const content = typeof binding.value === 'function' ? binding.value() : binding.value
    el.__bTooltip = setupTooltip(el, binding, content)
  },
  updated (el, binding) {
    const content = typeof binding.value === 'function' ? binding.value() : binding.value
    if (el.__bTooltip) {
      el.__bTooltip.update(content)
    }
  },
  unmounted (el) {
    if (el.__bTooltip) {
      el.__bTooltip.destroy()
      delete el.__bTooltip
    }
  }
}

const BCollapse = defineComponent({
  name: 'BCollapse',
  props: {
    id: String,
    modelValue: {
      type: Boolean,
      default: false
    },
    visible: {
      type: Boolean,
      default: undefined
    },
    tag: {
      type: String,
      default: 'div'
    }
  },
  emits: ['update:modelValue', 'input', 'update:visible'],
  computed: {
    isVisible () {
      return typeof this.visible === 'boolean' ? this.visible : this.modelValue
    }
  },
  render () {
    return h(
      this.tag,
      {
        id: this.id,
        class: ['collapse', { show: this.isVisible }]
      },
      this.$slots.default ? this.$slots.default() : []
    )
  }
})

const BAlert = defineComponent({
  name: 'BAlert',
  props: {
    show: {
      type: [Boolean, Number],
      default: true
    },
    variant: {
      type: String,
      default: 'info'
    }
  },
  render () {
    if (!this.show) return null
    return h(
      'div',
      {
        class: ['alert', `alert-${this.variant}`],
        role: 'alert'
      },
      this.$slots.default ? this.$slots.default() : []
    )
  }
})

const BTooltip = defineComponent({
  name: 'BTooltip',
  props: {
    target: {
      type: String,
      required: true
    },
    placement: {
      type: String,
      default: 'right'
    },
    triggers: {
      type: String,
      default: 'hover focus'
    },
    html: {
      type: Boolean,
      default: true
    }
  },
  setup (props, { slots, attrs }) {
    const cleanup = ref(null)
    onMounted(() => {
      const targetEl = document.getElementById(props.target)
      if (!targetEl) return
      const binding = {
        value: slots.default ? slots.default().map(v => v.children).join('') : '',
        modifiers: { html: props.html },
        arg: props.placement
      }
      const extraClass = attrs && attrs.class ? attrs.class : ''
      cleanup.value = setupTooltip(targetEl, binding, binding.value, extraClass)
    })
    onBeforeUnmount(() => {
      if (cleanup.value) cleanup.value.destroy()
    })
    return () => null
  }
})

export default {
  install (app) {
    app.component('b-collapse', BCollapse)
    app.component('b-alert', BAlert)
    app.component('b-tooltip', BTooltip)
    app.directive('b-tooltip', tooltipDirective)
  }
}
